import React from "react";
import { useApp } from "./store.jsx";
import { currentState, enable } from "./push.jsx";
// `ModalHost` is OURS — see chrome.jsx, where it portals a dialog to the body so
// it cannot be trapped by a stacking context. Dialog is ours too (Back closes
// it); only Button comes from the design system, and taking ModalHost from
// there gave `undefined`, which React renders by crashing the whole app.
import { ModalHost, Dialog } from "./chrome.jsx";

const { Button } = window.CardboardmarketDesignSystem_2a3239;

/**
 * ASKS WHY BEFORE ANDROID ASKS WHETHER.
 *
 * REPORTED, and it corrects something I got wrong the first time: "We're
 * interested in the user activating push notifications from the get-go, but we
 * should try to maximize the 'allows'. And that means not showing the user the
 * native dialog to accept before knowing why do we need him to enable them and
 * what kind of things he would receive."
 *
 * I had argued the settings screen was explanation enough. It is — for somebody
 * who has already walked to Settings to turn notifications on. That is not who
 * this is for. This is for everybody else, and the cost of getting it wrong is
 * not a missed opportunity: on Android 13 a refusal is effectively permanent,
 * so a system dialog shown at the wrong moment does not merely fail, it closes
 * the door.
 *
 * SO OUR DIALOG IS THE ONE THAT CAN BE SAID NO TO. "Not now" costs nothing and
 * can be asked again; Android's cannot. Nobody sees the system prompt here until
 * they have read what we would send and tapped a button that says to send it.
 *
 * ONLY IN THE ANDROID APP, deliberately. A browser has its own protections and
 * its own prompt, and the switch in settings is the honest route there. Here we
 * are holding the permission ourselves, which is exactly why the manners are
 * ours to get right.
 *
 * ONLY WHEN SIGNED IN. Every one of these is about something addressed to YOU —
 * a message, a trade waiting, a card on your wants list. Asking a guest to be
 * notified about their messages is asking about a thing they do not have.
 *
 * ONCE. The answer is remembered whichever way it goes, and Settings keeps the
 * switch for anybody who changes their mind. A prompt that comes back is the
 * thing people learn to dismiss without reading, which is how you lose the
 * permission you were trying to win.
 */
const ASKED = "cbm.push.invited";

function alreadyAsked() {
  try {
    return window.localStorage.getItem(ASKED) === "yes";
  } catch (error) {
    // Storage refused: treat it as asked. Better to never offer than to offer
    // on every single load, which is the behaviour that trains a person to say
    // no to everything.
    return true;
  }
}

function remember() {
  try { window.localStorage.setItem(ASKED, "yes"); } catch (error) { /* see above */ }
}

export function PushInvite() {
  const { trader, t, notify } = useApp();
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!trader || alreadyAsked()) return;
    let dead = false;
    /*
     * Asked of the real state rather than of the app's presence: somebody who
     * already has this on — a reinstall, or a second account on one phone —
     * must not be offered it again.
     */
    void currentState().then((state) => {
      if (dead) return;
      if (state.native && !state.subscribed) setOpen(true);
    });
    return () => { dead = true; };
  }, [trader]);

  if (!open) return null;

  const close = () => { remember(); setOpen(false); };

  return (
    <ModalHost>
      <Dialog open
        role="dialog"
        aria-label={t("Notifications")}
        title={t("Shall we tell you when something happens?")}
        description={t("Only these three, and only about you. You can change any of them later.")}
        closeLabel={t("Close")}
        width={420}
        onClose={close}
        footer={<>
          {/* "Not now" rather than "No": it is the truth — Settings keeps the
              switch — and a door somebody can come back through is worth more
              than one they have to be sure about today. */}
          <Button variant="ghost" onClick={close} disabled={busy}>{t("Not now")}</Button>
          <Button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await enable();
                notify(t("Notifications are on for this device."));
                remember();
                setOpen(false);
              } catch (error) {
                /*
                 * Remembered even when Android refuses, and this is the point of
                 * the whole screen: a refusal there is close to permanent, so
                 * asking again would be asking a question that can no longer be
                 * answered. Settings still says what to do about it.
                 */
                remember();
                setOpen(false);
                notify(t("Android did not allow notifications. You can turn them on in Settings."));
              } finally {
                setBusy(false);
              }
            }}
          >{t("Turn notifications on")}</Button>
        </>}>
        <ul style={{ margin: 0, paddingLeft: '1.1em', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {/* The three kinds, in our own words, in the order they matter to
              somebody trading: a person is waiting on them, then a person wrote
              to them, then the card they asked for exists. Each one is a thing
              that goes stale — which is the whole argument for interrupting
              anybody at all. */}
          <li>{t("Somebody writes to you about a card")}</li>
          <li>{t("Somebody is waiting on your answer to a trade")}</li>
          <li>{t("A card on your wants list turns up near you")}</li>
        </ul>
      </Dialog>
    </ModalHost>
  );
}
